import { logger } from '@/utils/logger';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AuditService } from '@/services/auditService';

export const useUserStatusMutations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Deactivate user mutation
  const deactivateUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      // Get the current session to pass the auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      logger.debug('Starting deactivation process for user:', userId);

      const { data: banData, error: banError } = await supabase.functions.invoke('ban-user', {
        body: { userId, ban: true },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (banError) {
        logger.error('Error calling ban-user function:', banError);
        throw new Error(`Failed to deactivate user: ${banError.message}`);
      }

      if (banData?.error) {
        logger.error('Error from ban-user function:', banData.error);
        throw new Error(banData.error);
      }

      logger.debug('User successfully deactivated:', banData);
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['usersWithRoles'] });
      
      // Log the status change
      await AuditService.logStatusChange(
        variables,
        'Active',
        'Disabled',
        'deactivate'
      );
      
      toast({
        title: "User deactivated",
        description: "User has been successfully deactivated and their profile status is now Disabled."
      });
    },
    onError: (error: any) => {
      logger.error('Deactivation error:', error);
      toast({
        title: "Error deactivating user",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Reactivate user mutation
  const reactivateUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      // Get the current session to pass the auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      logger.debug('Starting reactivation process for user:', userId);

      const { data: banData, error: banError } = await supabase.functions.invoke('ban-user', {
        body: { userId, ban: false },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (banError) {
        logger.error('Error calling ban-user function:', banError);
        throw new Error(`Failed to reactivate user: ${banError.message}`);
      }

      if (banData?.error) {
        logger.error('Error from ban-user function:', banData.error);
        throw new Error(banData.error);
      }

      logger.debug('User successfully reactivated:', banData);
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['usersWithRoles'] });
      
      // Log the status change
      await AuditService.logStatusChange(
        variables,
        'Disabled',
        'Active',
        'activate'
      );
      
      toast({
        title: "User reactivated",
        description: "User has been successfully reactivated and their profile status is now Active."
      });
    },
    onError: (error: any) => {
      logger.error('Reactivation error:', error);
      toast({
        title: "Error reactivating user",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      logger.debug('Attempting to delete user:', userId);
      
      // Get the current session to pass the auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      // Call the Edge Function with admin privileges
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { userId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        logger.error('Error calling delete-user function:', error);
        throw new Error(`Failed to delete user: ${error.message}`);
      }

      if (data?.error) {
        logger.error('Error from delete-user function:', data.error);
        throw new Error(data.error);
      }
      
      logger.debug('User successfully deleted from auth system');
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['usersWithRoles'] });
      
      // Log the deletion
      await AuditService.logStatusChange(
        variables,
        'Active',
        'Deleted',
        'delete'
      );
      
      toast({
        title: "User deleted",
        description: "User has been permanently deleted from the system."
      });
    },
    onError: (error: any) => {
      logger.error('Delete user error:', error);
      toast({
        title: "Error deleting user",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  return {
    deactivateUser: deactivateUserMutation.mutate,
    reactivateUser: reactivateUserMutation.mutate,
    deleteUser: deleteUserMutation.mutate,
  };
};
